
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Trash2, FileImage, Pin, PinOff } from "lucide-react";
import ArchiveIcon from "@/components/icons/ArchiveIcon";
import React, { useState } from "react";
import ManualThumbnailDialog from "../ManualThumbnailDialog";

interface CardActionMenuProps {
  itemId: string;
  itemTitle: string;
  isArchived?: boolean;
  isPinned?: boolean;
  onEdit?: (id: string, currentTitle: string) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  onPin?: (id: string) => void;
  onUnpin?: (id: string) => void;
  alignment?: "start" | "end";
  variant?: "icon" | "buttons";
}

/**
 * Consistent action menu for content cards across all views
 */
const CardActionMenu = ({
  itemId,
  itemTitle,
  isArchived = false,
  isPinned = false,
  onEdit,
  onArchive,
  onDelete,
  onPin,
  onUnpin,
  alignment = "end",
  variant = "icon",
}: CardActionMenuProps) => {
  const [showThumbnailDialog, setShowThumbnailDialog] = useState(false);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onEdit) {
      onEdit(itemId, itemTitle);
    }
  };

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onArchive) {
      onArchive(itemId);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onDelete) {
      onDelete(itemId);
    }
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    
    console.log(`[PIN DEBUG] CardActionMenu - Pin/Unpin clicked for item ${itemId}`);
    console.log(`[PIN DEBUG] CardActionMenu - isPinned: ${isPinned}, onPin: ${!!onPin}, onUnpin: ${!!onUnpin}`);
    
    if (isPinned && onUnpin) {
      console.log(`[PIN DEBUG] CardActionMenu - Calling onUnpin for item ${itemId}`);
      onUnpin(itemId);
    } else if (!isPinned && onPin) {
      console.log(`[PIN DEBUG] CardActionMenu - Calling onPin for item ${itemId}`);
      onPin(itemId);
    } else {
      console.warn(`[PIN DEBUG] CardActionMenu - No handler available for pin action`);
    }
  };

  const handleManualThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    console.log("[CardActionMenu] Opening thumbnail dialog for item:", itemId);
    setShowThumbnailDialog(true);
  };
  
  // Handle dialog state change
  const handleThumbnailDialogChange = (open: boolean) => {
    console.log("[CardActionMenu] Thumbnail dialog state changing to:", open);
    setShowThumbnailDialog(open);
    
    // If dialog is closing, refresh the current page without navigation
    if (!open && showThumbnailDialog) {
      console.log("[CardActionMenu] Dialog closing, will maintain current URL");
      const currentUrl = window.location.href;
      console.log("[CardActionMenu] Current URL:", currentUrl);
      setTimeout(() => {
        window.location.href = currentUrl;
      }, 300);
    }
  };

  // Debug logging for pin/unpin availability
  console.log(`[PIN DEBUG] CardActionMenu rendering for item ${itemId}:`, {
    isPinned,
    hasOnPin: !!onPin,
    hasOnUnpin: !!onUnpin,
    variant
  });

  // List view with separate buttons
  if (variant === "buttons") {
    return (
      <>
        <div className="flex justify-end space-x-2">
          {(onPin || onUnpin) && (
            <Button variant="outline" size="sm" onClick={handlePinClick}>
              {isPinned ? "Unpin" : "Pin to Top"}
            </Button>
          )}
          {onArchive && (
            <Button variant="outline" size="sm" onClick={handleArchiveClick}>
              {isArchived ? "Unarchive" : "Archive"}
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleManualThumbnailClick}
          >
            Thumbnail
          </Button>
          {onDelete && (
            <Button 
              variant="outline" 
              size="sm" 
              className="text-red-500" 
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          )}
        </div>
        <ManualThumbnailDialog 
          contentId={itemId}
          contentTitle={itemTitle}
          open={showThumbnailDialog}
          onOpenChange={handleThumbnailDialogChange}
        />
      </>
    );
  }

  // Icon dropdown menu for grid view - positioned in the content area, not on thumbnail
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-transparent p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-5 w-5 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={alignment}>
          {(onPin || onUnpin) && (
            <DropdownMenuItem onClick={handlePinClick}>
              {isPinned ? (
                <>
                  <PinOff className="mr-2 h-4 w-4" />
                  Unpin
                </>
              ) : (
                <>
                  <Pin className="mr-2 h-4 w-4" />
                  Pin to Top
                </>
              )}
            </DropdownMenuItem>
          )}
          {onArchive && (
            <DropdownMenuItem onClick={handleArchiveClick}>
              <ArchiveIcon className="mr-2 h-4 w-4" size={16} />
              {isArchived ? "Unarchive" : "Archive"}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleManualThumbnailClick}>
            <FileImage className="mr-2 h-4 w-4" />
            Manual thumbnail
          </DropdownMenuItem>
          {onDelete && (
            <DropdownMenuItem 
              onClick={handleDeleteClick}
              className="text-red-500 hover:text-red-500 focus:text-red-500"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ManualThumbnailDialog 
        contentId={itemId}
        contentTitle={itemTitle}
        open={showThumbnailDialog}
        onOpenChange={handleThumbnailDialogChange}
      />
    </>
  );
};

export default CardActionMenu;
